import type { ProductTier } from "./tiers";

export interface DisplayOptionConfig {
  id: string;
  label: string;
  description: string;
  preview: string;
  icon?: string;
  allowedFor: ProductTier[];
}

export interface SectionDisplayRegistry {
  presentations: DisplayOptionConfig[];
  emphases: DisplayOptionConfig[];
  defaultOptions: {
    presentation: string;
    emphasis: string;
  };
}

const standardEmphases: DisplayOptionConfig[] = [
  { id: "none", label: "None", description: "Default presentation", preview: "none_preview", allowedFor: ["starter", "pro", "premium"] },
  { id: "subtle", label: "Subtle", description: "Subtle background or border", preview: "subtle_preview", allowedFor: ["starter", "pro", "premium"] },
  { id: "strong", label: "Strong", description: "Strong background color", preview: "strong_preview", allowedFor: ["pro", "premium"] },
];

export const displayRegistry: Record<string, SectionDisplayRegistry> = {
  about: {
    presentations: [
      { id: "standard", label: "Standard", description: "Default text layout", preview: "std_preview", allowedFor: ["starter", "pro", "premium"] },
      { id: "compact", label: "Compact", description: "Denser text spacing", preview: "compact_preview", allowedFor: ["pro", "premium"] },
      { id: "split", label: "Split", description: "Text and highlights side-by-side", preview: "split_preview", allowedFor: ["premium"] },
    ],
    emphases: standardEmphases,
    defaultOptions: { presentation: "standard", emphasis: "none" },
  },
  publications: {
    presentations: [
      { id: "standard", label: "Standard", description: "Default publication list", preview: "std_preview", allowedFor: ["starter", "pro", "premium"] },
      { id: "compact_bibliography", label: "Compact bibliography", description: "A dense academic citation list.", preview: "compact_preview", allowedFor: ["pro", "premium"] },
      { id: "cards", label: "Cards", description: "Grid of publication cards", preview: "cards_preview", allowedFor: ["premium"] },
    ],
    emphases: standardEmphases,
    defaultOptions: { presentation: "standard", emphasis: "none" },
  },
  experience: {
    presentations: [
      { id: "timeline", label: "Timeline", description: "Chronological timeline view", preview: "timeline_preview", allowedFor: ["starter", "pro", "premium"] },
      { id: "cards", label: "Cards", description: "Grid of experience cards", preview: "cards_preview", allowedFor: ["pro", "premium"] },
      { id: "minimal", label: "Minimal", description: "Simple list", preview: "minimal_preview", allowedFor: ["starter", "pro", "premium"] },
    ],
    emphases: standardEmphases,
    defaultOptions: { presentation: "timeline", emphasis: "none" },
  },
  contact: {
    presentations: [
      { id: "standard", label: "Standard", description: "Default contact form", preview: "std_preview", allowedFor: ["starter", "pro", "premium"] },
      { id: "split", label: "Split", description: "Details and form side-by-side", preview: "split_preview", allowedFor: ["pro", "premium"] },
      { id: "minimal", label: "Minimal", description: "Simple contact links", preview: "minimal_preview", allowedFor: ["starter", "pro", "premium"] },
    ],
    emphases: standardEmphases,
    defaultOptions: { presentation: "standard", emphasis: "none" },
  },
  downloads: {
    presentations: [
      { id: "standard", label: "Standard", description: "List of files", preview: "std_preview", allowedFor: ["starter", "pro", "premium"] },
      { id: "grid", label: "Grid", description: "Grid of files", preview: "grid_preview", allowedFor: ["pro", "premium"] },
      { id: "list", label: "Compact List", description: "Dense list of files", preview: "list_preview", allowedFor: ["starter", "pro", "premium"] },
    ],
    emphases: standardEmphases,
    defaultOptions: { presentation: "standard", emphasis: "none" },
  },
  media: {
    presentations: [
      { id: "gallery", label: "Gallery", description: "Masonry gallery", preview: "gallery_preview", allowedFor: ["starter", "pro", "premium"] },
      { id: "carousel", label: "Carousel", description: "Horizontal swipe carousel", preview: "carousel_preview", allowedFor: ["pro", "premium"] },
      { id: "featured", label: "Featured", description: "One large featured item", preview: "featured_preview", allowedFor: ["pro", "premium"] },
    ],
    emphases: standardEmphases,
    defaultOptions: { presentation: "gallery", emphasis: "none" },
  },
  projects: {
    presentations: [
      { id: "grid", label: "Grid", description: "Grid of project cards", preview: "grid_preview", allowedFor: ["starter", "pro", "premium"] },
      { id: "list", label: "List", description: "List of projects", preview: "list_preview", allowedFor: ["starter", "pro", "premium"] },
      { id: "featured", label: "Featured", description: "Showcase single project", preview: "featured_preview", allowedFor: ["pro", "premium"] },
    ],
    emphases: standardEmphases,
    defaultOptions: { presentation: "grid", emphasis: "none" },
  },
  research: {
    presentations: [
      { id: "standard", label: "Standard", description: "Default research view", preview: "std_preview", allowedFor: ["starter", "pro", "premium"] },
      { id: "compact", label: "Compact", description: "Compact list", preview: "compact_preview", allowedFor: ["pro", "premium"] },
      { id: "cards", label: "Cards", description: "Research cards", preview: "cards_preview", allowedFor: ["premium"] },
    ],
    emphases: standardEmphases,
    defaultOptions: { presentation: "standard", emphasis: "none" },
  },
  skills: {
    presentations: [
      { id: "grid", label: "Grid", description: "Icon grid", preview: "grid_preview", allowedFor: ["starter", "pro", "premium"] },
      { id: "tags", label: "Tags", description: "Pill tags layout", preview: "tags_preview", allowedFor: ["starter", "pro", "premium"] },
      { id: "progress", label: "Progress", description: "Progress bars", preview: "progress_preview", allowedFor: ["pro", "premium"] },
    ],
    emphases: standardEmphases,
    defaultOptions: { presentation: "grid", emphasis: "none" },
  },
  teaching: {
    presentations: [
      { id: "standard", label: "Standard", description: "Default teaching view", preview: "std_preview", allowedFor: ["starter", "pro", "premium"] },
      { id: "compact", label: "Compact", description: "Compact list", preview: "compact_preview", allowedFor: ["pro", "premium"] },
      { id: "cards", label: "Cards", description: "Teaching cards", preview: "cards_preview", allowedFor: ["premium"] },
    ],
    emphases: standardEmphases,
    defaultOptions: { presentation: "standard", emphasis: "none" },
  },
};

/**
 * Checks if a user's tier permits them to use a specific display option (presentation or emphasis).
 */
export function isDisplayOptionAllowed(
  productTier: ProductTier,
  sectionType: string,
  optionType: "presentations" | "emphases",
  optionId: string
): boolean {
  const sectionRegistry = displayRegistry[sectionType];
  if (!sectionRegistry) return false;

  const option = sectionRegistry[optionType].find((o) => o.id === optionId);
  if (!option) return false;

  return option.allowedFor.includes(productTier);
}

/**
 * Resolves raw display options into validated, tier-enforced, defaulted options.
 */
export function resolveDisplayOptions(
  sectionType: string,
  rawOptions: { presentation?: string; emphasis?: string; [key: string]: any } | undefined | null,
  userTier: ProductTier = "starter"
): { presentation: string; emphasis: string } {
  const registry = displayRegistry[sectionType];
  if (!registry) {
    return { presentation: "standard", emphasis: "none" };
  }

  let presentation = rawOptions?.presentation || registry.defaultOptions.presentation;
  let emphasis = rawOptions?.emphasis || registry.defaultOptions.emphasis;

  if (!isDisplayOptionAllowed(userTier, sectionType, "presentations", presentation)) {
    presentation = registry.defaultOptions.presentation;
  }

  if (!isDisplayOptionAllowed(userTier, sectionType, "emphases", emphasis)) {
    emphasis = registry.defaultOptions.emphasis;
  }

  return { presentation, emphasis };
}
